/**
 * Canonical `items.contract_type` values — the single typed source of truth.
 *
 * Two independent classifiers (both in the docproc service,
 * `services/document-processing/src/`) write the same `items.contract_type` text column:
 *  - the IMAGE classifier (`pipeline/classifyImage.ts`) tags photographed uploads
 *    (`identity`, `contract-scan`, `receipt`, …);
 *  - the entity extractor (`lib/entityExtractor.ts`) tags the TEXT of real ingested
 *    documents (`employment`, `rental`, `services`, `other`).
 *
 * Every place that lists these values (the two classifiers, the search tool
 * schemas, and the workflow `documentTypes` registry) must reference the constants
 * here so a typo'd or stale value is a COMPILE error, not a silent runtime miss.
 */

/**
 * The two class lists are declared in `shared/vocabulary/crossService.ts` and re-exported here: the
 * classifiers that WRITE these values live in docproc, so both services must be shown the same
 * lists. See that file for why `identity` is one class. Everything below — the type guards, the
 * merged value list and the agent-facing filter description — is web-only.
 */
export {
  INGESTED_CONTRACT_TYPES, IMAGE_DOCUMENT_CLASSES,
  type IngestedContractType, type ImageDocumentClass,
} from './vocabulary/crossService'
import {
  INGESTED_CONTRACT_TYPES, IMAGE_DOCUMENT_CLASSES,
  type IngestedContractType, type ImageDocumentClass,
} from './vocabulary/crossService'

/** Is this `items.contract_type` an identity document? These are read via MRZ + printed-field
 *  extraction, NOT the OCR / Deep-Read text pipeline — they carry no transcript, so the doc viewer
 *  hides the Text tab for them (there is nothing to show but a "no content" message). */
export function isIdentityDocumentType(v: string | null | undefined): boolean {
  return v === 'identity'
}

/** Any value `items.contract_type` can hold, across both classifiers. */
export type ContractType = IngestedContractType | ImageDocumentClass

/** Deduped runtime list of every valid value (for building filter enums). */
export const CONTRACT_TYPE_VALUES: readonly ContractType[] =
  [...new Set<ContractType>([...INGESTED_CONTRACT_TYPES, ...IMAGE_DOCUMENT_CLASSES])]

/** Type guard: is a raw string a known contract_type value? */
export function isContractType(v: string | null | undefined): v is ContractType {
  return v != null && (CONTRACT_TYPE_VALUES as readonly string[]).includes(v)
}

/**
 * What the `contract_type` filter means, for the two document-search tools the chat agent calls
 * (`server/tools/docSearch.ts`, `server/tools/documents.ts`). One constant because the two used to
 * carry hand-maintained copies of the same list and had already drifted — one hardcoded the enum
 * while the other derived it, and both still described `sv-dui-front`/`sv-dui-back`/`id-other`.
 *
 * English, per the prompt mandate: the model reasons in English, and the Spanish user phrasings it
 * has to recognise are quoted as data rather than the instruction being written in Spanish.
 */
export const CONTRACT_TYPE_FILTER_DESCRIPTION = `Filter by document type (prefer over free-text query when a category is named):
- "identity" — any ID document of any country or side (DUI, DPI/CUI, cédula, DNI, pasaporte, carnet de residencia, driver's licence; never filter by country or front/back)
- "employment" — employment contract ("contrato de trabajo", "contrato laboral", "empleo")
- "rental" — lease ("contrato de arrendamiento", "alquiler", "renta")
- "services" — services agreement ("contrato de servicios", "prestación de servicios", "honorarios")
- "poder" — power of attorney ("poder", "mandato")
- "receipt" — receipt or invoice ("recibo", "comprobante de pago", "factura")
- "contract-scan" — scanned/photographed legal document ("contrato escaneado")
- "photo" — non-document photograph ("foto", "imagen")
- "other" — anything else
- "all" — no filter`

